import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { AlertOctagon, ShieldAlert, Clock3, Gauge, Radio } from 'lucide-react';
import { fmtDate } from '@/components/OpsUI';

const EVENT_META = {
  'error.5xx': { icon: AlertOctagon, color: 'text-red-400', ring: 'bg-red-500/15 border-red-500/30' },
  'security.lockout': { icon: ShieldAlert, color: 'text-amber-400', ring: 'bg-amber-500/15 border-amber-500/30' },
  'payment.stuck': { icon: Clock3, color: 'text-orange-400', ring: 'bg-orange-500/15 border-orange-500/30' },
  'alert.error_rate': { icon: Gauge, color: 'text-red-300', ring: 'bg-red-500/15 border-red-500/30' },
  'alert.payment_backlog': { icon: Gauge, color: 'text-orange-300', ring: 'bg-orange-500/15 border-orange-500/30' },
};

function summarize(ev, p = {}) {
  switch (ev) {
    case 'error.5xx': return `${p.method || ''} ${p.path || ''} · ${p.exc_type || ''}`.trim();
    case 'security.lockout': return `${p.scope || ''}: ${p.email || p.ip || ''}`;
    case 'payment.stuck': return `${p.amount_ves ?? '?'} VES · ${p.stuck_minutes ?? '?'} min · ${p.status || ''}`;
    case 'alert.error_rate': return `${p.count ?? '?'} / ${p.max_count ?? '?'} en ${p.window_minutes ?? '?'} min`;
    case 'alert.payment_backlog': return `${p.count ?? '?'} / ${p.max_count ?? '?'} aportes atascados`;
    default: return typeof p === 'object' && p !== null
      ? Object.keys(p).slice(0, 3).map((k) => `${k}:${typeof p[k] === 'object' && p[k] !== null ? JSON.stringify(p[k]) : p[k]}`).join(' · ')
      : String(p);
  }
}

export default function LiveFeed({ pollMs = 12000 }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [pulse, setPulse] = useState(false);
  const lastTop = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/ops/events', { params: { limit: 40 } });
      const items = Array.isArray(data) ? data : [];
      if (items.length && items[0].id !== lastTop.current) {
        if (lastTop.current !== null) { setPulse(true); setTimeout(() => setPulse(false), 1200); }
        lastTop.current = items[0].id;
      }
      setEvents(items);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  return (
    <Card className="bg-card border border-border" data-testid="ops-live-feed">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 ${pulse ? 'animate-ping' : ''}`} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <div>
            <div className="text-sm font-display font-semibold flex items-center gap-1.5"><Radio className="h-4 w-4 text-[hsl(var(--primary))]" /> {t('feed_title')}</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('feed_subtitle')}</div>
          </div>
        </div>
        <span className="text-[11px] font-mono text-[hsl(var(--muted-foreground))]">{events.length}</span>
      </div>
      <div className="max-h-[320px] overflow-auto divide-y divide-border/40">
        {events.length === 0 ? (
          <div className="p-5 text-xs text-[hsl(var(--muted-foreground))]">{t('feed_empty')}</div>
        ) : events.map((e) => {
          const meta = EVENT_META[e.event] || { icon: Radio, color: 'text-[hsl(var(--primary))]', ring: 'bg-[hsl(var(--muted))] border-border' };
          const Icon = meta.icon;
          return (
            <div key={e.id} className="px-4 py-3 flex items-start gap-3 hover:bg-accent/40" data-testid={`feed-event-${e.id}`}>
              <div className={`h-7 w-7 rounded-full border flex items-center justify-center flex-shrink-0 ${meta.ring}`}>
                <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono font-medium truncate">{e.event || t('feed_event')}</span>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] whitespace-nowrap">{fmtDate(e.received_at)}</span>
                </div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate mt-0.5">{summarize(e.event, e.payload)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
